import React, { useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useUser } from "./hooks/useUsers";
import { useHotels } from "@/components/pages/hotels/hooks/useHotels";
import { UserForm } from "@/components/pages/users/components/UserForm";
import { UserCustomRolesPanel } from "@/components/pages/users/components/UserCustomRolesPanel";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { hasTenantPermission } from "@/utils/accessControl";

export const UserDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAuthenticatedUser();

  // "View" (read-only) roles reach this screen too, so editing (including the
  // custom-roles panel) requires the administrate permission — regardless of
  // the `?edit=true` query param, which a read-only user could craft manually.
  const canEditUsers = hasTenantPermission(userProfile, "users:administrate");
  const isEditing = !!searchParams.get("edit") && canEditUsers;
  const isOwnProfile = Boolean(id) && userProfile?.id === id;
  const canManageCustomRoles = canEditUsers && !isOwnProfile;

  const { data: user, isLoading: isLoadingUser } = useUser(id || "");
  const { data: hotelsResponse } = useHotels();
  const hotels = hotelsResponse?.data || [];

  const onUserFormBack = useCallback(() => navigate("/users"), [navigate]);

  if (!isLoadingUser && !user) return null;

  return (
    <>
      <UserForm
        hotels={hotels}
        user={user}
        isEditing={isEditing}
        onBack={onUserFormBack}
      />
      {user && !isEditing && canManageCustomRoles ? (
        <UserCustomRolesPanel userId={user.id} userRole={user.role} />
      ) : null}
    </>
  );
};
